import type { FormEvent } from 'react'
import type { DemandInputName, Demand } from 'types/buyer'

import { useState, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import styled from 'styled-components'

import { clientPanelPaths, basePaths } from 'paths'
import { tomorrow, fiveYearsFromNow } from 'common'
import { useStore } from 'store'
import useIsClientPage from 'hooks/useIsClientPage'
import copy from 'copy.json'
import { adminPanelPaths } from 'paths'
import {
  addParamToPath,
  dimmBackground,
  getInput,
  getValue,
  leaveOnlyNumbers,
  trimFormData
} from 'utils'
import { addDemandFormAutoFill } from 'utils/devTools'
import { validFileExtension, validFileExtensions } from 'utils/validators'
import { validateDemandForm } from 'utils/pageValidators'

import Button from 'components/Button'
import InfoTooltip from 'components/InfoTooltip'
import Input, { PrefixedInput } from 'components/Input'
import InputLabel from 'components/InputLabel'
import Spinner from 'components/Spinner'
import AddResourceContent from 'components/AddResourceContent'
import Checkbox, { CheckboxLabel } from 'components/Checkbox'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { energyProfileCsvGuide } from 'components/csvFormatGuides'

const text = copy.buyer.add_demand
const form_text = copy.common.form

export default function AddDemandScreen() {
  const { api, currentUser, clearFieldErrors, fieldErrors } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isClientPage = useIsClientPage()
  const [loading, setLoading] = useState(false)
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [noProfileFileChecked, setNoProfileFileChecked] = useState(false)
  const [errors, setErrors] = useState<{ [key in DemandInputName]?: string }>({})

  const buyerId = location.state

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget

    if (!input) return

    const validation = validateDemandForm(input)

    setErrors(state => ({ ...state, [input.name]: validation }))

    return validation
  }

  const isFileValid = (file: File) => {
    if (validFileExtension(file.name) !== undefined) {
      setErrors(state => ({ ...state, profileFile: copy.validators.file_extension }))
      return false
    }

    if (file.size > 25000000) {
      setErrors(state => ({ ...state, profileFile: copy.validators.file_too_big }))
      return false
    }

    return true
  }

  const handleProfileFileChange = (event: FormEvent<HTMLInputElement>) => {
    const currentTarget = event.currentTarget
    const file = currentTarget.files?.[0]

    if (!file || !isFileValid(file)) return

    setProfileFile(file)
  }

  const getCity = async (zipCode: string) => {
    const input: HTMLInputElement | null = document.querySelector('[name="location_city"]')

    const city = await api.getCity(zipCode, isClientPage)

    if (!input || !city) return

    input.value = city
    setErrors(state => ({ ...state, location_city: undefined }))
  }

  const onProfileFileCheckboxChange = (event: FormEvent<HTMLInputElement>) => {
    setNoProfileFileChecked(event.currentTarget.checked)
    if (event.currentTarget.checked) {
      setProfileFile(null)
    }
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (loading || !(currentUser || isClientPage)) return

    const form = event.currentTarget
    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateDemandForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      return
    }

    setLoading(true)

    const requestData: Partial<Demand> = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({
        ...result,
        [name]: value === '' || value === '0' ? null : value
      }),
      {}
    )

    if (currentUser) {
      requestData.buyer_id = currentUser.id
    }

    requestData.yearly_power_consumption = parseFloat(String(requestData.yearly_power_consumption))
    requestData.contract_length = parseFloat(String(requestData.contract_length))
    requestData.energy_multiplier = parseFloat(String(requestData.energy_multiplier))
    requestData.current_energy_price = parseFloat(String(requestData.current_energy_price))
    requestData.location_zip_code = leaveOnlyNumbers(requestData.location_zip_code)

    // @ts-ignore
    delete requestData.profileFile

    const formData = new FormData()

    formData.append(
      'request',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )

    if (profileFile) formData.append('profileFile', profileFile)

    const response = await api.addDemand(isClientPage, formData)

    setLoading(false)

    if (response && buyerId) {
      navigate(addParamToPath(adminPanelPaths.buyer.search, buyerId))
      return
    }

    if (response && isClientPage) {
      navigate(clientPanelPaths.confirmation)
      return
    }

    if (response) navigate(adminPanelPaths.buyer.search)
  }

  useEffect(() => {
    dimmBackground()
    clearFieldErrors()
  }, [clearFieldErrors])

  useEffect(() => {
    if (Object.keys(errors).length) {
      getInput(Object.keys(fieldErrors)[0])?.scrollIntoView({ behavior: 'smooth' })
    }
    setErrors(fieldErrors)
  }, [fieldErrors]) //eslint-disable-line

  useEffect(() => {
    if (errors.profileFile) {
      setProfileFile(null)
      const fileInput = document.querySelector<HTMLInputElement>('[name="profileFile"]')
      if (fileInput) fileInput.value = ''
    }
  }, [errors])

  if (!currentUser && !isClientPage) return <Navigate to={basePaths.admin_panel} />

  return (
    <AddResourceContent as="form" noValidate onSubmit={submit}>
      {process.env.NODE_ENV === 'development' && (
        <button type="button" onClick={addDemandFormAutoFill}>
          Auto fill
        </button>
      )}

      <h1>{text.heading}</h1>
      <p>{text.paragraph}</p>

      <InputLabel>
        <span>{form_text.osd_name}</span>
        <span>{errors.osd_name}</span>
        <Input name="osd_name" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span>{form_text.electric_energy_meter_number}</span>
        <span>{errors.electric_energy_meter_number}</span>
        <Input name="electric_energy_meter_number" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span>{form_text.energy_point_number}</span>
        <span>{errors.energy_point_number}</span>
        <Input name="energy_point_number" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {form_text.energy_tariff_group}
          <InfoTooltip>{form_text.energy_tariff_group_hint}</InfoTooltip>
        </span>
        <span>{errors.energy_tariff_group}</span>
        <Input name="energy_tariff_group" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {form_text.energy_multiplier}
          <InfoTooltip>{form_text.energy_multiplier_hint}</InfoTooltip>
        </span>
        <span>{errors.energy_multiplier}</span>
        <Input
          name="energy_multiplier"
          type="number"
          step={1}
          min={1}
          max={1000000}
          defaultValue="1"
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{form_text.yearly_power_consumption}</span>
        <span>{errors.yearly_power_consumption}</span>
        <PrefixedInput
          name="yearly_power_consumption"
          type="number"
          prefix={copy.common.MWh}
          prefixWidth="50px"
          reverse
          step={1}
          min={1}
          max={1000000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{form_text.current_energy_price}</span>
        <span>{errors.current_energy_price}</span>
        <PrefixedInput
          name="current_energy_price"
          type="number"
          prefix={copy.common.zl}
          prefixWidth="28px"
          reverse
          min={1}
          step={0.01}
          max={10000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{text.form.current_energy_provider_name}</span>
        <span>{errors.current_energy_provider_name}</span>
        <Input name="current_energy_provider_name" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span>{form_text.current_contract_end_date}</span>
        <span>{errors.current_contract_end_date}</span>
        <Input
          type="date"
          name="current_contract_end_date"
          min={tomorrow}
          max={fiveYearsFromNow}
          onBlur={event => {
            validateInput(event)
            getValue('contract_start_date') &&
              validateInput(undefined, getInput('contract_start_date'))
          }}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{form_text.contract_start_date}</span>
        <span>{errors.contract_start_date}</span>
        <Input
          type="date"
          name="contract_start_date"
          min={tomorrow}
          max={fiveYearsFromNow}
          onBlur={event => {
            validateInput(event)
            getValue('current_contract_end_date') &&
              validateInput(undefined, getInput('current_contract_end_date'))
          }}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{text.form.contract_length}</span>
        <span>{errors.contract_length}</span>
        <PrefixedInput
          type="number"
          name="contract_length"
          prefix={copy.common.months_abbr}
          prefixWidth="52px"
          reverse
          min={1}
          max={240}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <Subheader>{text.subheading}</Subheader>

      <InputLabel>
        <span>{form_text.location_zip_code}</span>
        <span>{errors.location_zip_code}</span>
        <InputMask
          mask="99-999"
          maskPlaceholder={null}
          onInput={event => !validateInput(event) && getCity(event.currentTarget.value)}
          onBlur={validateInput}
        >
          <Input name="location_zip_code" style={{ width: '50%' }} />
        </InputMask>
      </InputLabel>

      <InputLabel>
        <span>{form_text.location_city}</span>
        <span>{errors.location_city}</span>
        <Input name="location_city" onBlur={validateInput} />
      </InputLabel>

      <div>
        <TooltipWrapper>
          {text.form.profile_file}
          <InfoTooltip>{text.form.profile_file_hint}</InfoTooltip>
        </TooltipWrapper>

        {!noProfileFileChecked && (
          <InputLabel hideErrorOnFocus={false}>
            <span /> {/* Empty span for correct errors styling */}
            <span>{errors.profileFile}</span>
            <Input
              name="profileFile"
              type="file"
              accept={validFileExtensions.join(',')}
              onBlur={event =>
                document.activeElement !== event.currentTarget && validateInput(event)
              }
              onChange={handleProfileFileChange}
            />
            <CsvFormatGuide definition={energyProfileCsvGuide} />
          </InputLabel>
        )}

        <CheckboxLabelStyled>
          <Checkbox checked={noProfileFileChecked} onChange={onProfileFileCheckboxChange} />
          {form_text.no_profile_file}
        </CheckboxLabelStyled>
      </div>

      <Button type="submit">{loading ? <Spinner size={16} /> : text.button}</Button>

      <Button secondary onClick={() => navigate(-1)} type="button">
        {copy.common.button.go_back}
      </Button>
    </AddResourceContent>
  )
}

const Subheader = styled.h4`
  font: var(--600-16);
`

const CheckboxLabelStyled = styled(CheckboxLabel)`
  color: var(--gray);
  margin: 10px 0 10px 0;

  ${Checkbox} {
    width: 24px;
    height: 24px;
    border-color: var(--light-gray);
  }
`

const TooltipWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--gray);
`
